
import { FileText, BarChart } from 'lucide-react';
import React from 'react';
import { ReportData, MonthlySummaryData, QuarterlyCostData, AnnualUsageData } from './types';

export const monthlySummaryData: MonthlySummaryData = {
  title: "Monthly Energy Summary - April 2023",
  overview: {
    totalConsumption: "12,845 kWh",
    totalCost: "$1,542.35",
    comparedToPrevious: "3.2% lower than March",
    averageDailyCost: "$51.41",
    peakDemandDay: "April 15",
    peakDemandValue: "580 kWh"
  },
  consumptionBreakdown: [
    { category: "HVAC Systems", percentage: 42, consumption: "5,395 kWh", cost: "$647.79" },
    { category: "Lighting", percentage: 18, consumption: "2,312 kWh", cost: "$277.62" },
    { category: "Equipment", percentage: 25, consumption: "3,211 kWh", cost: "$385.59" },
    { category: "Office Devices", percentage: 10, consumption: "1,285 kWh", cost: "$154.24" },
    { category: "Other", percentage: 5, consumption: "642 kWh", cost: "$77.11" }
  ],
  dailyReadings: [
    { date: "Apr 1", consumption: 420, cost: 50.40 },
    { date: "Apr 2", consumption: 405, cost: 48.60 },
    { date: "Apr 10", consumption: 455, cost: 54.60 },
    { date: "Apr 15", consumption: 580, cost: 69.60 },
    { date: "Apr 20", consumption: 410, cost: 49.20 },
    { date: "Apr 30", consumption: 425, cost: 51.00 }
  ],
  recommendations: [
    "Optimize HVAC scheduling to reduce consumption during off-hours",
    "Consider lighting upgrades to more efficient LED fixtures",
    "Inspect equipment for maintenance needs to improve efficiency"
  ]
};

export const quarterlyCostData: QuarterlyCostData = {
  title: "Quarterly Cost Analysis - Q1 2023",
  overview: {
    totalConsumption: "37,290 kWh",
    totalCost: "$4,475.86",
    comparedToPrevious: "5.8% higher than Q4 2022",
    highestMonth: "February",
    highestMonthValue: "13,510 kWh",
    averageMonthlyCost: "$1,491.95"
  },
  monthlyBreakdown: [
    { month: "January", consumption: 11815, cost: 1417.80, percentageOfTotal: 31.7 },
    { month: "February", consumption: 13510, cost: 1621.20, percentageOfTotal: 36.2 },
    { month: "March", consumption: 11965, cost: 1436.86, percentageOfTotal: 32.1 }
  ],
  facilityComparison: [
    { facility: "Main Office", consumption: 15650, cost: 1878.00, efficiency: "+12%" },
    { facility: "Manufacturing Plant A", consumption: 12840, cost: 1540.80, efficiency: "+8%" },
    { facility: "Manufacturing Plant B", consumption: 8800, cost: 1057.06, efficiency: "-3%" }
  ],
  costFactors: [
    { factor: "Base Energy Rate", percentage: 75, amount: "$3,356.90" },
    { factor: "Peak Demand Charges", percentage: 18, amount: "$805.65" },
    { factor: "Taxes & Fees", percentage: 7, amount: "$313.31" }
  ]
};

export const annualUsageData: AnnualUsageData = {
  title: "Annual Usage Report - 2022",
  overview: {
    totalConsumption: "156,742 kWh",
    totalCost: "$18,809.04",
    comparedToPrevious: "1.3% higher than 2021",
    averageMonthlyCost: "$1,567.42",
    highestConsumptionMonth: "July (16,890 kWh)",
    lowestConsumptionMonth: "April (10,120 kWh)"
  },
  seasonalAnalysis: [
    { season: "Winter (Dec-Feb)", consumption: 43650, cost: 5238.00, percentageOfTotal: 27.8 },
    { season: "Spring (Mar-May)", consumption: 32760, cost: 3931.20, percentageOfTotal: 20.9 },
    { season: "Summer (Jun-Aug)", consumption: 47680, cost: 5721.60, percentageOfTotal: 30.4 },
    { season: "Fall (Sep-Nov)", consumption: 32652, cost: 3918.24, percentageOfTotal: 20.8 }
  ],
  yearOverYearComparison: [
    { year: 2020, consumption: 151320, cost: 18158.40 },
    { year: 2021, consumption: 154730, cost: 18567.60 },
    { year: 2022, consumption: 156742, cost: 18809.04 }
  ],
  sustainabilityMetrics: {
    carbonFootprint: "68.97 metric tons CO2e",
    renewablePercentage: "22% of total consumption",
    carbonReduction: "3.2% reduction from energy efficiency measures"
  }
};

// Create the file icons as functions to avoid JSX in TypeScript files
const createFileTextIcon = () => React.createElement(FileText, { className: "h-5 w-5 text-blue-400" });
const createBarChartIcon = () => React.createElement(BarChart, { className: "h-5 w-5 text-blue-400" });

export const getSavedReports = (): ReportData[] => {
  return [
    {
      id: "report-1",
      title: "Monthly Energy Summary",
      date: "April 10, 2023",
      fileType: "PDF",
      fileIcon: createFileTextIcon(),
      description: "Detailed summary of energy consumption patterns and costs for April 2023",
      dataPreview: {
        total: "12,845 kWh",
        cost: "$1,542.35",
        trend: "3.2% lower than March",
        trendType: "positive",
        insight: "HVAC systems accounted for 42% of total usage"
      },
      detailedData: monthlySummaryData
    },
    {
      id: "report-2",
      title: "Quarterly Cost Analysis",
      date: "March 31, 2023",
      fileType: "Excel",
      fileIcon: createBarChartIcon(),
      description: "Financial analysis of Q1 2023 energy expenditures by department and facility",
      dataPreview: {
        total: "37,290 kWh",
        cost: "$4,475.86",
        trend: "5.8% higher than Q4 2022",
        trendType: "negative",
        insight: "Manufacturing facilities showed 12% increased efficiency"
      },
      detailedData: quarterlyCostData
    },
    {
      id: "report-3",
      title: "Annual Usage Report",
      date: "February 15, 2023",
      fileType: "CSV",
      fileIcon: createFileTextIcon(),
      description: "Comprehensive breakdown of 2022 energy consumption with year-over-year comparisons",
      dataPreview: {
        total: "156,742 kWh",
        cost: "$18,809.04",
        trend: "1.3% higher than 2021",
        trendType: "negative",
        insight: "Summer cooling resulted in 23% of annual energy costs"
      },
      detailedData: annualUsageData
    }
  ];
};
